import React, { useCallback, useMemo, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import ModalContext from 'contexts/ModalContext'

const USPItem = styled(Clickable)`
  display: block;

  & >:last-child {
    display: inline;
    vertical-align: middle;
  }
`
interface Props {
  offer: App.BundleOffer
  className?: string
}

type ModalTypes = 'bestPrice'

function BundleAndSaveUSPs(props: Props) {
  const { offer, className } = props
  const intl = useIntl()

  const showModal = useContext(ModalContext)
  const onItemClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const modalType = e.currentTarget.dataset.type as ModalTypes
    const modalTitle = intl.formatMessage({
      id: '2Hm1TW8fsk',
      defaultMessage: 'Our best price guarantee',
      description: 'Modal title for best price guarantee',
    })
    showModal(<Modal
      title={modalTitle}
    >
      {modalType === 'bestPrice' && <VerticalSpacer gap={16}>
        <BodyText variant="large">
          {intl.formatMessage({
            id: '/4zYQJB7t7',
            defaultMessage: 'We negotiate the best prices so you don’t have to. In the unlikely event that you find a better price for the same offer, we promise to match it.',
            description: 'Best price guarantee explanation text',
          })}
        </BodyText>
        <BodyText variant="large">
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'OZdOZtqSZx',
              defaultMessage: '1. Compare the same offer',
              description: 'Step 1 heading for best price guarantee process',
            })}
          </Heading>
          {intl.formatMessage({
            id: 'Nx7k9qxQHp',
            defaultMessage: 'Make sure the offer you’ve found is for the same hotel with the same VIP inclusions as our package.',
            description: 'Step 1 description for best price guarantee process',
          })}
        </BodyText>
        <BodyText variant="large">
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'n+5LsQW4Ud',
              defaultMessage: '2. Record the better price',
              description: 'Step 2 heading for best price guarantee process',
            })}
          </Heading>
          {intl.formatMessage({
            id: 'iJxF8zrBBj',
            defaultMessage: 'Take a website screenshot or a photo of a catalogue so you can send it to us.',
            description: 'Step 2 description for best price guarantee process',
          })}
        </BodyText>
        <BodyText variant="large">
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'TUcTc00/RN',
              defaultMessage: '3. We’ll match the price',
              description: 'Step 3 heading for best price guarantee process',
            })}
          </Heading>
          {intl.formatMessage({
            id: 'y2AK/Kaas4',
            defaultMessage: 'Once verified we’ll match the price.',
            description: 'Step 3 description for best price guarantee process',
          })}
        </BodyText>
      </VerticalSpacer>}
      <HelpPane />
    </Modal>)
  }, [showModal, intl])

  const disableBestPriceGuarantee = useMemo(() => !Object.values(offer.bundledOffers).some((o) => o.type === 'hotel' && !o.disableBestPriceGuarantee), [offer.bundledOffers])

  return (<VerticalSpacer gap={8} className={className}>
    {config.bestPriceGuarantee && !disableBestPriceGuarantee && <USPItem
      data-type="bestPrice"
      onClick={onItemClick}
    >
      <BodyText
        variant="medium"
        colour="highlight-secondary"
        weight="bold"
        as="span"
      >
        {intl.formatMessage({
          id: '6n9FK3CQih',
          defaultMessage: 'Best price guarantee',
          description: 'Best price guarantee text',
        })}
      </BodyText>
      <LineInfoCircleIcon colour="neutral-one" size="XS" />
    </USPItem>}
  </VerticalSpacer>)
}

export default React.memo(BundleAndSaveUSPs)
